import { useUserStore } from '@talon/web/src/context';
import { Anchor, Box, Button, Layer, Nav, ResponsiveContext, Text } from 'grommet';
import { Close, Home, Menu as MenuIcon, UserExpert } from 'grommet-icons';
import TalonLogo from 'jsx:@talon/web/public/images/talon-ai-logo.svg';
import { observer } from 'mobx-react-lite';
import { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AgencyMenus, AnchorLink, ProjectSelector } from '.';
import { NotificationsMenu } from '../ui';
import { QuickEstimatorMenu } from '../ui/quick-estimator/QuickEstimatorMenu';
import { UserMenu } from './UserMenu';

export const HeaderNav = observer(() => {
  /** Context **/
  const userStore = useUserStore();
  const navigate = useNavigate();
  const size = useContext(ResponsiveContext);
  const isSmall = size === 'small';

  /** State **/
  const [menuOpen, setMenuOpen] = useState(false);

  const homeRoute = userStore.user?.performer_id
    ? '/performer-home'
    : userStore.user?.is_talent_manager
      ? '/talent-manager'
      : '/';

  const logo = (
    <Anchor as={Button} onClick={() => navigate(homeRoute)}>
      <Box direction="row" gap="xsmall" align="center">
        <Box justify="center">
          <TalonLogo width="40" height="40" />
        </Box>
        <Box margin={{ bottom: '2px' }} direction="row" align="center">
          <Text weight={400} style={{ letterSpacing: '0.2rem' }}>
            TALON
          </Text>
        </Box>
      </Box>
    </Anchor>
  );

  // Everything below the logo that isn't a single icon button — the project/agency mega-menu,
  // the performer's and talent manager's own nav links, the quick estimator, and the user menu.
  // On a full-width header these sit in a row; at phone width they'd overlap into an illegible
  // pile (the header used to do exactly that), so on `small` they're stacked in a slide-out
  // drawer instead.
  // accent-1 (white) is meant for the blue header bar — on the drawer's light background it
  // would be invisible, so these get a dark color explicitly when rendered there.
  const linkColor = isSmall ? 'text' : 'accent-1';
  const navLinks = (
    <>
      {userStore.user?.is_talent_manager && (
        <AnchorLink
          to={'/talent-manager'}
          label={<Text color={linkColor}>My Dashboard</Text>}
          icon={<UserExpert size="20" color={linkColor} />}
          alignSelf="center"
        />
      )}
      {userStore.user?.id && !userStore.user?.performer_id && (
        <Box direction={isSmall ? 'column' : 'row'} gap="medium" align={isSmall ? 'start' : 'center'}>
          <ProjectSelector textColor={linkColor} />
          <AgencyMenus textColor={linkColor} iconColor={linkColor} />
        </Box>
      )}
      {userStore.user?.performer_id && (
        <AnchorLink
          to={'/performer-home'}
          label={<Text color={linkColor}>My Commercials</Text>}
          icon={<Home size="20" color={linkColor} />}
          alignSelf="center"
        />
      )}
    </>
  );

  if (isSmall) {
    return (
      <Nav
        direction="row"
        background={{ color: 'brand-gradient', dark: true }}
        style={{ background: 'var(--brand-gradient)', zIndex: 2 }}
        fill="horizontal"
        height="72px"
        justify="between"
        pad={{ horizontal: 'small' }}
        align="center"
        elevation="small"
      >
        {logo}
        <Box direction="row" gap="small" align="center">
          {userStore.user?.id && <NotificationsMenu />}
          <Button
            icon={<MenuIcon color="white" />}
            onClick={() => setMenuOpen(true)}
            a11yTitle="Open menu"
            plain
          />
        </Box>
        {menuOpen && (
          <Layer position="right" full="vertical" onEsc={() => setMenuOpen(false)} onClickOutside={() => setMenuOpen(false)}>
            <Box width="min(85vw, 340px)" height="100%" background="light-1" pad="medium" gap="medium" overflow="auto">
              <Box direction="row" justify="end">
                <Button icon={<Close />} onClick={() => setMenuOpen(false)} a11yTitle="Close menu" plain />
              </Box>
              <Box gap="medium">{navLinks}</Box>
              {userStore.user?.active_group && (
                // QuickEstimatorMenu's icon is hardcoded white for the blue header bar — give it
                // a matching chip here instead, rather than reworking a shared component's colors.
                <Box background="brand" round="small" width="40px" height="40px" align="center" justify="center">
                  <QuickEstimatorMenu />
                </Box>
              )}
              <UserMenu textColor="text" />
            </Box>
          </Layer>
        )}
      </Nav>
    );
  }

  /** Render **/
  return (
    <Nav
      direction="row"
      background={{ color: 'brand-gradient', dark: true }}
      style={{ background: 'var(--brand-gradient)', zIndex: 2 }}
      fill="horizontal"
      flex="grow"
      height="94px"
      justify="between"
      pad={{ horizontal: 'medium' }}
      align="center"
      elevation="small"
    >
      <Box width="10%">{logo}</Box>

      <Box flex />
      {navLinks}
      <Box flex />
      <Box direction="row" gap="small" justify="end" align="center" flex="grow">
        {userStore.user?.active_group && <QuickEstimatorMenu />}
        {userStore.user?.id && <NotificationsMenu />}
        <UserMenu />
      </Box>
    </Nav>
  );
});
